import type React from 'react';
import { useCallback, useMemo } from 'react';
import type { DayButton } from 'react-day-picker';
import { Calendar, CalendarDayButton } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { dateToIsoKey, isoDateKey, isoKeyToLocalDate, isoKeyYear } from '@/utils/dateUtils';
import type { Holiday } from './HolidayDrawer';

const MONTH_INDICES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;

function formatHolidayTooltip(holiday: Holiday): string {
  const pct = (holiday.not_working_percentage * 100).toFixed(1);
  return `${holiday.name} — ${pct}% not working`;
}

interface HolidayYearCalendarProps {
  year: number;
  holidays: Holiday[];
  selectedDate?: Date;
  onDateClick: (date: Date, holiday: Holiday | null) => void;
}

export function HolidayYearCalendar({
  year,
  holidays,
  selectedDate,
  onDateClick,
}: HolidayYearCalendarProps) {
  const yearHolidays = useMemo(
    () => holidays.filter((h) => h.date && isoKeyYear(h.date) === year),
    [holidays, year]
  );

  const holidaysByDate = useMemo(() => {
    const map = new Map<string, Holiday>();
    for (const h of yearHolidays) {
      map.set(isoDateKey(h.date), h);
    }
    return map;
  }, [yearHolidays]);

  const holidayDates = useMemo(
    () => yearHolidays.map((h) => isoKeyToLocalDate(h.date)),
    [yearHolidays]
  );

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      if (!date) return;
      const key = dateToIsoKey(date);
      onDateClick(date, holidaysByDate.get(key) ?? null);
    },
    [holidaysByDate, onDateClick]
  );

  const dayButtonComponent = useMemo(
    () => ({
      DayButton: ({
        modifiers,
        day,
        className,
        ...props
      }: React.ComponentProps<typeof DayButton>) => {
        const holiday = holidaysByDate.get(dateToIsoKey(day.date));
        const isHoliday = holiday && !modifiers.outside;

        return (
          <CalendarDayButton
            day={day}
            modifiers={modifiers}
            title={isHoliday ? formatHolidayTooltip(holiday) : undefined}
            className={cn(
              isHoliday &&
                'bg-pink-500 text-white hover:bg-pink-500 hover:text-white data-[selected-single=true]:bg-pink-600 data-[selected-single=true]:text-white',
              className
            )}
            {...props}
          >
            <span>{day.date.getDate()}</span>
          </CalendarDayButton>
        );
      },
    }),
    [holidaysByDate]
  );

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
      {MONTH_INDICES.map((monthIndex) => {
        const monthDate = new Date(year, monthIndex, 1);
        return (
          <div key={monthIndex} className="flex flex-col gap-1">
            <p className="text-sm font-medium text-text-primary">
              {monthDate.toLocaleString('default', { month: 'long' })}
            </p>
            <Calendar
              mode="single"
              month={monthDate}
              onMonthChange={() => {}}
              selected={selectedDate}
              onSelect={handleSelect}
              hideNavigation
              disableNavigation
              showOutsideDays={false}
              className="w-full p-1 [--cell-size:--spacing(7)]"
              classNames={{
                month_caption: 'hidden',
                nav: 'hidden',
              }}
              modifiers={{ holiday: holidayDates }}
              components={dayButtonComponent}
            />
          </div>
        );
      })}
    </div>
  );
}
